import React, { useEffect, useCallback } from 'react';
import './Modal.css';

/**
 * General-purpose modal.
 *
 * Props:
 *  title       string
 *  message     string
 *  type        'confirm' | 'alert' | 'error'  (default 'confirm')
 *  confirmLabel string  (default 'Confirm')
 *  cancelLabel  string  (default 'Cancel')
 *  danger       bool    — makes confirm button red
 *  onConfirm   fn      — called when user clicks the confirm/ok button
 *  onCancel    fn      — called when user cancels (confirm type only)
 *  onClose     fn      — alias for onCancel / close
 */
export default function Modal({
  title,
  message,
  type = 'confirm',
  confirmLabel,
  cancelLabel = 'Cancel',
  danger = false,
  onConfirm,
  onCancel,
  onClose,
}) {
  const handleClose = useCallback(() => {
    if (onCancel) onCancel();
    else if (onClose) onClose();
  }, [onCancel, onClose]);
  const handleConfirm = onConfirm || handleClose;

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') handleClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [handleClose]);

  const defaultConfirmLabel = type === 'alert' || type === 'error' ? 'OK' : 'Confirm';
  const label = confirmLabel || defaultConfirmLabel;

  const icon = type === 'error' ? '⚠️'
    : type === 'alert' ? 'ℹ️'
    : danger ? '🗑️'
    : '❓';

  return (
    <div
      className="modal-dialog-overlay"
      onClick={(e) => e.target === e.currentTarget && handleClose()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div className="modal-dialog">
        <div className="modal-dialog__icon">{icon}</div>

        {title && <h2 className="modal-dialog__title" id="modal-title">{title}</h2>}

        {message && <p className="modal-dialog__message">{message}</p>}

        <div className="modal-dialog__actions">
          {type === 'confirm' && (
            <button className="btn btn-ghost" onClick={handleClose}>
              {cancelLabel}
            </button>
          )}
          <button
            className={`btn ${danger ? 'btn-danger' : 'btn-primary'}`}
            onClick={handleConfirm}
            autoFocus
          >
            {label}
          </button>
        </div>
      </div>
    </div>
  );
}
